import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Bell, BellRing, CheckCheck, AlertTriangle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const POLL_INTERVAL_MS = 300_000; // 5 minutes

const BudgetAlertBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await apiFetch("/budget-alerts?unread_only=true");
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const unreadCount = alerts.filter((a) => !a.is_read).length;

  const markRead = async (alertId) => {
    try {
      await apiFetch(`/budget-alerts/${alertId}/read`, { method: "PATCH" });
      setAlerts((prev) => prev.map((a) => (a.alert_id === alertId ? { ...a, is_read: true } : a)));
    } catch {
      // non-fatal
    }
  };

  const markAllRead = async () => {
    try {
      await apiFetch("/budget-alerts/read-all", { method: "PATCH" });
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    } catch {
      // non-fatal
    }
  };

  if (alerts.length === 0 && !open) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label="Budget alerts"
          data-testid="budget-alert-bell"
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-amber-500" />
          ) : (
            <Bell className="w-5 h-5 text-brand-steel" />
          )}
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border">
          <span className="text-sm font-semibold text-brand-ink">Budget Alerts</span>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 text-xs text-brand-steel hover:text-brand-ink transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {alerts.length === 0 ? (
            <p className="text-sm text-brand-steel text-center py-6">No budget alerts</p>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.alert_id}
                className={`flex items-start gap-3 px-4 py-3 border-b border-brand-border transition-colors ${
                  alert.is_read ? "bg-brand-panel" : "bg-amber-50 dark:bg-amber-950/30"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    alert.threshold >= 1.0 ? "text-red-500" : "text-amber-500"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-ink leading-snug">{alert.message}</p>
                  <p className="text-xs text-brand-steel mt-0.5">
                    {alert.total_hours}h / {alert.budget_hours}h
                  </p>
                </div>
                {!alert.is_read && (
                  <button
                    onClick={() => markRead(alert.alert_id)}
                    className="text-xs text-brand-steel hover:text-brand-ink flex-shrink-0 mt-0.5"
                    title="Mark as read"
                  >
                    ✓
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BudgetAlertBell;
